import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useOrganizationStore } from '@/stores/organization'
import { usePermissions } from '@/composables/usePermissions'
import { consumeTenantQuery } from '@/lib/tenantQuery'

// Layouts
import AuthenticatedLayout from '@/components/layout/AuthenticatedLayout.vue'
import OrganizationLayout from '@/components/layout/OrganizationLayout.vue'
import GuestLayout from '@/components/layout/GuestLayout.vue'

const router = createRouter({
  history: createWebHistory(),
  routes: [
    {
      path: '/',
      component: AuthenticatedLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: 'organizations/select',
          name: 'organization-selection',
          component: () => import('@/views/console/OrganizationSelectionView.vue'),
        },
        {
          path: '/',
          component: OrganizationLayout,
          meta: { requiresOrganization: true },
          children: [
            // The planning board IS the dashboard — the workspace root lands there.
            {
              path: '',
              name: 'home',
              redirect: '/planning',
            },
            {
              path: 'contacts',
              name: 'contacts',
              component: () => import('@/views/console/contacts/ContactsIndexView.vue'),
            },
            {
              path: 'contacts/:id',
              name: 'contact-show',
              component: () => import('@/views/console/contacts/ContactShowView.vue'),
              props: true
            },
            {
              path: 'companies',
              name: 'companies',
              component: () => import('@/views/console/companies/CompaniesIndexView.vue'),
            },
            {
              path: 'companies/:id',
              name: 'company-show',
              component: () => import('@/views/console/companies/CompanyShowView.vue'),
              props: true,
            },
            {
              path: 'locations',
              name: 'locations',
              component: () => import('@/views/console/locations/LocationsIndexView.vue'),
            },
            {
              path: 'locations/:id',
              name: 'location-show',
              component: () => import('@/views/console/locations/LocationShowView.vue'),
              props: true,
            },
            {
              path: 'jobs',
              name: 'jobs',
              component: () => import('@/views/console/jobs/JobsIndexView.vue'),
            },
            {
              path: 'planning',
              name: 'planning',
              component: () => import('@/views/console/planning/PlanningBoardView.vue'),
              // The desk used to be the board's third FACE (?view=pipeline).
              // It has its own route now, so old links, bookmarks and toasts
              // land where the desk actually lives.
              beforeEnter: (to) => (to.query.view === 'pipeline' ? { name: 'pipeline-sales' } : true),
            },
            // THE PIPELINE IN THREE PAGES (his ruling, 2026-10-27) — flat
            // routes under VENTES, beside Leads and Soumissions: Ventes (the
            // sales queue, by reason), Renouvellements (the campaign — a cohort
            // of customers), Paiements (what is owed). The two lists are ONE
            // component told its page; /pipeline lands on Ventes so old links
            // and bookmarks keep working.
            {
              path: 'pipeline',
              name: 'pipeline',
              redirect: { name: 'pipeline-sales' },
            },
            {
              path: 'pipeline/sales',
              name: 'pipeline-sales',
              component: () => import('@/views/console/pipeline/PipelineListView.vue'),
              props: { page: 'sales' },
            },
            {
              path: 'pipeline/payments',
              name: 'pipeline-payments',
              component: () => import('@/views/console/pipeline/PipelineListView.vue'),
              props: { page: 'payments' },
            },
            {
              path: 'pipeline/renewals',
              name: 'pipeline-renewals',
              component: () => import('@/views/console/pipeline/PipelineRenewalsView.vue'),
            },
            // « Leads » — a FRONTEND route over the jobs resource (filter[phase]=lead),
            // not a new entity. There is deliberately no /leads/:id: a lead is a job
            // in its early phase, and a detail route would change the deal's URL the
            // moment it signs. Rows open /jobs/:id like everything else.
            {
              path: 'leads',
              name: 'leads',
              component: () => import('@/views/console/leads/LeadsIndexView.vue'),
            },
            // The SAME detail view under the sales path, so clicking a lead does
            // not throw you into « Travaux ». Still NO Lead entity and no second
            // component — one deal, one page, two doors.
            // ⚠ The page CANONICALISES itself: open a won deal here and it
            // replaces the URL with /jobs/:id (and vice versa). That is what
            // keeps the old objection dead — a deal can never sit at a URL that
            // contradicts its phase, and every existing /jobs/:id link survives.
            {
              path: 'leads/:id',
              name: 'lead-show',
              component: () => import('@/views/console/jobs/JobShowView.vue'),
              props: true,
            },
            {
              path: 'jobs/:id',
              name: 'job-show',
              component: () => import('@/views/console/jobs/JobShowView.vue'),
              props: true
            },
            {
              path: 'proposals',
              name: 'proposals',
              component: () => import('@/views/console/proposals/ProposalsIndexView.vue'),
            },
            // The builder is a top-level SOUMISSION page — looking at a
            // document lights « Soumissions » in the sidebar, not « Travaux ».
            {
              path: 'proposals/:id',
              name: 'proposal-builder',
              component: () => import('@/views/console/jobs/ProposalBuilderView.vue'),
              props: true
            },
            {
              path: 'parts',
              name: 'parts',
              component: () => import('@/views/console/parts/PartsIndexView.vue'),
            },
            {
              path: 'parts/:id',
              name: 'part-show',
              component: () => import('@/views/console/parts/PartShowView.vue'),
              props: true,
            },
            {
              path: 'fleet',
              name: 'fleet',
              component: () => import('@/views/console/fleet/FleetIndexView.vue'),
            },
            {
              path: 'fleet/crews/:id',
              name: 'crew-show',
              component: () => import('@/views/console/fleet/CrewShowView.vue'),
              props: true,
            },
            // « Saisons » — the Flotte pattern: the index READS (name, state,
            // year), the season page ACTS (every run verb lives there). Reads
            // are open to every member; the page hides its verbs from a plain
            // member (the server refuses the writes).
            {
              path: 'seasons',
              name: 'seasons',
              component: () => import('@/views/console/seasons/SeasonsIndexView.vue'),
            },
            {
              path: 'seasons/:id',
              name: 'season-show',
              component: () => import('@/views/console/seasons/SeasonShowView.vue'),
              props: true,
            },
            // ONE YEAR of a season — its two statuses (campaign · season), its
            // verbs and its facts. Keyed by YEAR, not by run: a year may have
            // no run yet (« Lancer la campagne 2027 » births it).
            {
              path: 'seasons/:id/:year(\\d{4})',
              name: 'season-year',
              component: () => import('@/views/console/seasons/SeasonYearView.vue'),
              props: true,
            },
            {
              path: 'templates',
              name: 'templates',
              component: () => import('@/views/console/templates/JobTemplatesIndexView.vue'),
            },
            {
              path: 'templates/:id',
              name: 'template-show',
              component: () => import('@/views/console/templates/JobTemplateShowView.vue'),
              props: true,
            },
            // « Importations » — a tool's run history, not a setting: it left
            // the settings tree so the tree could close to non-admins without
            // closing the door on a dispatcher importing contacts.
            // ⚠ It belongs HERE, inside OrganizationLayout. Sitting one level
            // up made it a sibling of the layout instead of a child, so the
            // page rendered with no app nav at all — clicking it in the
            // sidebar made the sidebar disappear.
            {
              path: 'imports',
              name: 'imports',
              component: () => import('@/views/console/imports/ImportsIndexView.vue'),
            },
          ]
        },
        // Personal settings — its own layout (own sidebar) that replaces the app nav.
        // No organization requirement (matches the original /settings behaviour).
        {
          path: 'settings',
          component: () => import('@/views/console/settings/SettingsLayout.vue'),
          children: [
            { path: '', redirect: { name: 'settings-profile' } },
            {
              path: 'profile',
              name: 'settings-profile',
              component: () => import('@/views/console/SettingsView.vue'),
            },
            {
              path: 'notifications',
              name: 'settings-notifications',
              component: () => import('@/views/console/settings/NotificationSettingsView.vue'),
            },
          ],
        },
        // Organization settings — its own layout (own sidebar). Org-scoped.
        {
          path: 'organizations/settings',
          component: () => import('@/views/console/settings/OrganizationSettingsLayout.vue'),
          // requiresAdmin at the ROOT: every settings write is refused to a plain
          // member server-side (require_org_admin! on organizations, payments,
          // closures, taxes, sectors; the composer's two attributes on
          // squads#update), so the whole tree is an admin's. One list — the
          // sidebar filter reads the same meta — so guard and menu cannot drift.
          // Importations is not a setting and lives under /imports; the seasons
          // are a resource and live under /seasons.
          meta: { requiresOrganization: true, requiresAdmin: true },
          children: [
            { path: '', redirect: { name: 'settings-organization' } },
            {
              path: 'general',
              name: 'settings-organization',
              component: () => import('@/views/console/OrganizationSettingsView.vue'),
            },
            {
              path: 'paper',
              name: 'settings-paper',
              component: () => import('@/views/console/settings/BusinessPaperSettingsView.vue'),
            },
            {
              path: 'members',
              name: 'settings-members',
              component: () => import('@/views/console/settings/MembersSettingsView.vue'),
            },
            {
              path: 'hours',
              name: 'settings-organization-hours',
              component: () => import('@/views/console/settings/BusinessHoursSettingsView.vue'),
            },
            {
              path: 'weather',
              name: 'settings-weather',
              component: () => import('@/views/console/settings/WeatherSettingsView.vue'),
            },
            {
              path: 'closures',
              name: 'settings-closures',
              component: () => import('@/views/console/settings/ClosuresSettingsView.vue'),
            },
            {
              path: 'taxes',
              name: 'settings-taxes',
              component: () => import('@/views/console/settings/OrganizationTaxesView.vue'),
            },
            {
              path: 'payments',
              name: 'settings-payments',
              component: () => import('@/views/console/settings/PaymentsSettingsView.vue'),
            },
            {
              path: 'sectors',
              name: 'settings-sectors',
              component: () => import('@/views/console/settings/SectorsSettingsView.vue'),
            },
          ],
        },
        // Backwards-compat redirects for older / interim URLs.
        { path: 'squads', redirect: { name: 'fleet' } },
        { path: 'vehicles', redirect: { name: 'fleet', query: { tab: 'vehicles' } } },
        { path: 'settings/organization', redirect: { name: 'settings-organization' } },
        { path: 'settings/taxes', redirect: { name: 'settings-taxes' } },
        { path: 'settings/imports', redirect: { name: 'imports' } },
        { path: 'organizations/settings/imports', redirect: { name: 'imports' } },
        { path: 'organizations/settings/periods', redirect: { name: 'seasons' } },
      ]
    },
    {
      path: '/',
      component: GuestLayout,
      meta: { isGuest: true },
      children: [
        {
          path: 'login',
          name: 'login',
          component: () => import('@/views/shared/LoginView.vue'),
        },
        {
          path: 'forgot-password',
          name: 'forgot-password',
          component: () => import('@/views/shared/ForgotPasswordView.vue'),
        },
        {
          path: 'reset-password',
          name: 'reset-password',
          component: () => import('@/views/shared/ResetPasswordView.vue'),
        },
        {
          path: 'accept-invite',
          name: 'accept-invite',
          component: () => import('@/views/shared/AcceptInviteView.vue'),
        },
        {
          path: 'auth/callback',
          name: 'auth-callback',
          component: () => import('@/views/shared/AuthCallbackView.vue')
        }
      ]
    }
  ]
})

router.beforeEach(async (to, _from) => {
  const auth = useAuthStore()
  const orgStore = useOrganizationStore()

  // 1. Blocking boot: the first navigation waits until the profile is loaded so
  //    protected views can read auth.currentUser without null-guards. Web is
  //    cookie-only — no native secure-storage hydration. On a page refresh the
  //    in-memory JWT is gone, so try the HttpOnly refresh cookie.
  if (!auth.isBooted) {
    try {
      if (!auth.isAuthenticated) {
        const refreshed = await auth.tryRefreshToken()
        if (refreshed) await auth.fetchMe()
      }
    } finally {
      auth.isBooted = true
    }
  }

  // 2. Defensive: authenticated but the profile isn't loaded yet (e.g. the OAuth
  //    callback set the token, or a prior nav booted without a profile). Await
  //    fetchMe() before any protected route renders so currentUser is guaranteed.
  if (auth.isAuthenticated && !auth.user) {
    await auth.fetchMe()
  }

  // 3. Fetch Organizations if authenticated but not yet loaded
  if (auth.isAuthenticated && orgStore.organizations.length === 0) {
    await orgStore.fetchOrganizations()
  }

  // A notification opened in a fresh tab names its tenant in the URL; select
  // it and re-enter the route without the key.
  if (auth.isAuthenticated) {
    const retargeted = consumeTenantQuery(to, orgStore)
    if (retargeted) return retargeted
  }

  // 4. Guards
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return '/login'
  }

  // Check if organization is required for the route or any of its parents
  const needsOrg = to.matched.some(record => record.meta.requiresOrganization)
  if (needsOrg && !orgStore.currentOrganizationId) {
    if (auth.isAuthenticated) {
      return '/organizations/select'
    }
  }

  // Every sign-in door lands on the picker; with a single workspace there is
  // nothing to pick (fetchOrganizations already selected it), so go home.
  if (to.name === 'organization-selection' && orgStore.organizations.length === 1 && orgStore.currentOrganizationId) {
    return '/'
  }

  // Organization settings mirror the server's role gate: a plain member is sent
  // home before the form renders, the same silence the API would answer with.
  const needsAdmin = to.matched.some(record => record.meta.requiresAdmin)
  if (needsAdmin) {
    const { isAdmin } = usePermissions()
    if (!isAdmin.value) return '/'
  }

  if (to.name === 'login' && auth.isAuthenticated) {
    return '/'
  }
})

export default router
